import { Injectable, inject } from '@angular/core';
import type { ExclusivePriceGrid, SaveExclusivePricesRequest } from '@models/exclusive-prices';
import type { WaterPriceList } from '@models/water-price-list';
import { CrmWaterExclusivePricesService } from '@services/crm-water-exclusive-prices.service';
import { CrmWaterPeriodsService } from '@services/crm-water-periods.service';
import { CrmWaterPriceListsService } from '@services/crm-water-price-lists.service';
import { forkJoin, map, type Observable } from 'rxjs';
import type { ExclusivePricingInitialData } from './exclusive-pricing.model';

/**
 * Data-access layer for {@link ExclusivePricingComponent}: assembles the one-shot initial load and hides
 * the grid endpoints. Purely request-building — error toasts, spinners and state live in the caller.
 */
@Injectable({ providedIn: 'root' })
export class ExclusivePricingDataService {
  private readonly exclusiveService = inject(CrmWaterExclusivePricesService);
  private readonly priceListsService = inject(CrmWaterPriceListsService);
  private readonly periodsService = inject(CrmWaterPeriodsService);

  loadInitial(waterId: number): Observable<ExclusivePricingInitialData> {
    return forkJoin({
      priceLists: this.priceListsService.getPriceLists(waterId),
      dimensions: this.periodsService.getPricingDimensions(waterId),
    }).pipe(
      map(({ priceLists, dimensions }) => ({
        priceLists,
        seasons: dimensions.seasons,
        stayTypes: dimensions.stayTypes,
      })),
    );
  }

  /**
   * The price lists alone, for adopting a change made in the Price lists section without re-reading the
   * grid the user may be part-way through editing.
   */
  getPriceLists(waterId: number): Observable<WaterPriceList[]> {
    return this.priceListsService.getPriceLists(waterId);
  }

  /** Load the full grid (all seasons × stay types) for one price list. */
  getGrid(waterId: number, priceListId: number): Observable<ExclusivePriceGrid> {
    return this.exclusiveService.getGrid(waterId, priceListId);
  }

  /** PUT the whole grid for one price list (the API reconciles to minimal writes). */
  saveGrid(waterId: number, priceListId: number, request: SaveExclusivePricesRequest): Observable<ExclusivePriceGrid> {
    return this.exclusiveService.saveGrid(waterId, priceListId, request);
  }
}
